// #ux-redesign-start: AuthLayout (Paso 5 — Auth redesign)
// Layout compartido por LoginPage, RegisterPage, PasswordRecovery.
//
// Estructura:
//   .ds-auth-page              (viewport - navbar, flex column, no overflow)
//     .ds-auth                 (flex row, height 100%)
//       .ds-auth__panel        (full height del page, llega al bottom; foto de fondo + textos abajo)
//       .ds-auth__main         (flex column, full height)
//         .ds-auth__main-inner (form centrado, padding propio, scroll si overflow)
//         {legalBar}           (inside main, full-width de main, sin padding lateral)
//
// El `legalBar` vive DENTRO de `__main` para que su borde top alinee con el
// borde right del `__panel` en el bottom-left corner del main. Como `__main`
// no tiene padding-x (lo lleva `__main-inner`), la barra ocupa los 100% del
// ancho de la columna main sin gaps.
//
// AuthLayout agrega `body.ds-auth-active` mientras está montado para que el
// bridge neutralice el `.full-page` legacy (overlay blanco + min-height 100vh).
//
// PORTABILIDAD: trasplantable a Web-Patients.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

const DEFAULT_IMAGE = '/General/login/hero-patients.jpg';

// Logo blanco de marca. Se usa como <img> y no inline: el SVG trae su propio
// <style> con clases genéricas (.st0, .st1) que inline se filtrarían al resto
// de la página.
const BRAND_LOGO = '/General/login/logo-white.svg';

const AuthLayout = ({
  children,
  panelTitle,
  panelSubtitle,
  panelBrand = false,
  image = DEFAULT_IMAGE,
  showPanel = true,
  panelContent = null,
  legalBar = null,
}) => {
  useEffect(() => {
    document.body.classList.add('ds-auth-active');
    return () => document.body.classList.remove('ds-auth-active');
  }, []);

  // Con `panelBrand` el logo ocupa el lugar del título: es la marca, no un
  // texto que la nombre. Las vistas con un título propio (ayuda, 2FA) lo
  // conservan, porque ahí el título dice dónde está el usuario.
  const heading = panelBrand ? (
    <img className="ds-auth__panel-logo" src={BRAND_LOGO} alt="" aria-hidden="true" />
  ) : (
    panelTitle && <h2 className="ds-auth__panel-title">{panelTitle}</h2>
  );

  return (
    <div className="ds-auth-page">
      <div className="ds-auth">
        {showPanel && (
          <aside className="ds-auth__panel" aria-hidden={!panelContent}>
            {panelContent ? (
              <div className="ds-auth__panel-content">{panelContent}</div>
            ) : (
              <>
                <img
                  className="ds-auth__panel-media"
                  src={image}
                  alt=""
                  aria-hidden="true"
                  decoding="async"
                />
                <div className="ds-auth__panel-overlay" aria-hidden="true" />
                {(heading || panelSubtitle) && (
                  <div className="ds-auth__panel-text">
                    {heading}
                    {panelSubtitle && (
                      <p className="ds-auth__panel-subtitle">{panelSubtitle}</p>
                    )}
                  </div>
                )}
              </>
            )}
          </aside>
        )}
        <main className="ds-auth__main">
          <div className="ds-auth__main-inner">{children}</div>
          {legalBar}
        </main>
      </div>
    </div>
  );
};

AuthLayout.propTypes = {
  children: PropTypes.node.isRequired,
  panelTitle: PropTypes.node,
  panelSubtitle: PropTypes.node,
  panelBrand: PropTypes.bool,
  image: PropTypes.string,
  showPanel: PropTypes.bool,
  panelContent: PropTypes.node,
  legalBar: PropTypes.node,
};

export default AuthLayout;
// #ux-redesign-end
